import Seo from '../components/Seo';
import InquiryForm from '../components/InquiryForm';
import { brand } from '../config/brand';

const risks = [
  {
    title: 'Operational downtime',
    risk: 'Inconsistent chemistry and stock-outs stall housekeeping, kitchens and production lines.',
    mitigation:
      'Concentrated, batch-consistent formulations and dependable supply keep programmes running predictably, shift after shift.',
  },
  {
    title: 'Failed inspections',
    risk: 'Audit failures and non-compliance findings carry penalties, remediation cost and reputational damage.',
    mitigation:
      'Documented, specification-controlled products with full ingredient transparency support your hygiene and compliance evidence.',
  },
  {
    title: 'Worker safety & liability',
    risk: 'Harsh, poorly labelled chemicals expose staff to injury and expose the business to liability.',
    mitigation:
      'Skin-neutral, allergen-screened and clearly documented chemistries are designed to reduce handling risk on the floor.',
  },
  {
    title: 'Supply-chain volatility',
    risk: 'Fragmented, multi-vendor sourcing amplifies price swings and continuity risk.',
    mitigation:
      'A consolidated, single-manufacturer programme stabilises specification, pricing structure and continuity of supply.',
  },
];

export default function AssurancePage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Operational Assurance — Uclipse',
    url: `${brand.siteUrl}/assurance`,
    description:
      'Operational Assurance from Uclipse: premium cleaning chemistry framed as insurance against operational failure — protecting uptime, inspections and worker safety.',
    isPartOf: { '@type': 'WebSite', name: 'Uclipse', url: `${brand.siteUrl}/` },
  };

  return (
    <>
      <Seo
        title="Operational Assurance"
        description="Premium chemistry as insurance against operational failure. Uclipse helps protect uptime, inspections and worker safety with consistent, compliant, documented supply."
        canonicalUrl={`${brand.siteUrl}/assurance`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(55% 70% at 85% 0%, rgba(14,91,168,0.5) 0%, rgba(2,6,23,0) 60%)',
          }}
        />
        <div className="container-page relative max-w-4xl py-20 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ring-1 ring-white/20 backdrop-blur">
            Operational Assurance
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight md:text-5xl lg:text-6xl">
            The lowest price is the highest risk
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            For enterprise operations, cleaning chemistry isn’t a commodity line item — it’s a control against
            downtime, failed inspections and liability. A premium programme is insurance you can measure in avoided
            failures.
          </p>
        </div>
      </section>

      {/* Risk cards */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
            Four risks a premium programme mitigates
          </h2>
          <p className="mt-3 text-slate-600">
            High-yield, consistent, documented chemistry is engineered to reduce the failure modes that cost the
            most.
          </p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {risks.map((r) => (
            <article key={r.title} className="card overflow-hidden">
              <div className="border-b border-slate-100 bg-slate-50 px-6 py-4">
                <h3 className="font-display text-lg font-semibold text-slate-900">{r.title}</h3>
              </div>
              <div className="space-y-4 p-6">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-red-500">The risk</div>
                  <p className="mt-1 text-sm text-slate-600">{r.risk}</p>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-accent-green">
                    How Uclipse helps
                  </div>
                  <p className="mt-1 text-sm text-slate-700">{r.mitigation}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Price as insurance */}
      <section className="border-y border-slate-100 bg-slate-50 py-16">
        <div className="container-page grid items-center gap-8 md:grid-cols-2">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-brand-600">The premium rationale</span>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 md:text-4xl">
              Price signals quality — and buys certainty
            </h2>
          </div>
          <p className="text-slate-600">
            A higher specification should promise more than performance; it should promise fewer surprises.
            Consistent chemistry, dependable supply and complete documentation convert an unpredictable cost centre
            into a managed, auditable control. That certainty is the product.
          </p>
        </div>
      </section>

      {/* Dossier request */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-2xl">
          <div className="mb-8 text-center">
            <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
              Request the technical dossier
            </h2>
            <p className="mt-3 text-slate-600">
              Our enterprise technical dossier covers specifications, safety and compliance documentation, dilution
              economics and programme design for your sector. Tell us about your operation and we’ll prepare and
              send the relevant pack.
            </p>
          </div>
          <InquiryForm
            type="quote"
            title="Request the technical dossier"
            description="Share your facility type, scale and priorities so we can tailor the documentation we send."
          />
        </div>
      </section>
    </>
  );
}
